import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    AXNUIHeaderContainer,
    AXNUIFooterContainer
} from "@iddataweb/idw-common-ui/containers";
import {
    getConfigurationLayout,
    getConfigurationTheme,
    getIDPName,
    getLogoPosition,
    getLogoUrl,
    getPageFavicon,
    getRPName,
    getUseThemedUI
} from "@redux/selectors/verification-configuration-selectors";
import { CSSProperties, useEffect, useMemo } from "react";
import { loadFont, transform } from "@iddataweb/idw-common-ui/utils";
import { useFavicon } from "@hooks/use-favicon";
import { MainContainer } from "../main/main-container";
import { getShowAsyncModal } from "@redux/selectors/verification-form-selectors";
import { OverlayContainer } from "../overlay/overlay-container";
import { AppVersion } from "../app-version/app-version";
import { getSubmissionState } from "@redux/selectors/submission-selectors";
import { getCustomHeaderHtml, getCustomFooterHtml } from "@redux/selectors/common-selectors";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { getQueryParametersFlow } from "@redux/selectors/query-parameters-selectors";
import { navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import styles from "./content-container.less";

export const ContentContainer = () => {
    const theme = useSelector(getConfigurationTheme);
    const layout = useSelector(getConfigurationLayout);
    const content = useSelector(getLanguageContent);
    const rpName = useSelector(getRPName);
    const idpName = useSelector(getIDPName);
    const pageFavicon = useSelector(getPageFavicon);
    const submissionState = useSelector(getSubmissionState);
    const showAsyncModal = useSelector(getShowAsyncModal);
    const useThemedUI = useSelector(getUseThemedUI);
    const customHeaderHtml = useSelector(getCustomHeaderHtml);
    const customFooterHtml = useSelector(getCustomFooterHtml);
    const v2LogoUrl = useSelector(getLogoUrl);
    const v2LogoPosition = useSelector(getLogoPosition);
    const queryParameterFlow = useSelector(getQueryParametersFlow);

    const navigate = useNavigate();

    useEffect(() => {
        if (theme.customCss?.useCustomCss) {
            const existingStyle = document.getElementById(`user-supplied-custom-css`);

            if (!existingStyle) {
                const output = transform(theme?.customCss?.cssContent, `#root-html`, '', false);
                const style = document.createElement('style');
                style.setAttribute("id", `user-supplied-custom-css`);
                style.setAttribute("type", "text/css");
                style.appendChild(document.createTextNode(output));
                document.head.appendChild(style);
            }
        }
    }, [theme]);

    useEffect(() => {
        if (theme.general?.font) {
            loadFont(theme.general.font, 'axn_font');
        }
    }, [theme.general?.font]);

    useEffect(() => {
        if (
            queryParameterFlow &&
            !location.pathname.includes(queryParameterFlow) &&
            !location.pathname.includes("error")
        ) {
            const flowPath = queryParameterFlow.startsWith("/") ? queryParameterFlow : `/${queryParameterFlow}`;
            navigateToUtilWithQueryParameters(flowPath, navigate, location.pathname, location.search);
        }
    }, []);

    useFavicon(theme.general?.faviconUrl || pageFavicon);

    const getHeaderProps = () => {
        return {
            ...theme.header,
            ...content?.header,
            useThemedUI,
            customHeaderHtml,
            idpName,
            relyingPartyName: rpName,
            defaultTextColor: theme.general.mainTextColor,
            defaultFontFamily: theme.general.font,
            logoAltText: content?.header?.logoAltText,
            v2LogoPosition,
            v2LogoUrl
        };
    };

    const getFooterProps = () => {
        return {
            ...theme.footer,
            ...content?.footer,
            useThemedUI,
            customFooterHtml,
            defaultTextColor: theme.general.mainTextColor,
            defaultFontFamily: theme.general.font
        };
    };

    const showOverlayContainer = useMemo(() => {
        return !showAsyncModal && submissionState.processing;
    }, [showAsyncModal, submissionState.processing]);


    const contentContainerStyle: CSSProperties = useThemedUI ? {
        fontFamily: theme.general?.font,
        color: theme.general?.mainTextColor,
    } : undefined;

    return (
        <div id={"content-container"} className={styles.contentContainer} style={contentContainerStyle}>
            <AppVersion />
            {((useThemedUI && layout.isHeaderEnabled) || customHeaderHtml || (v2LogoUrl && v2LogoPosition === "top")) && (
                <AXNUIHeaderContainer {...getHeaderProps()} />
            )}
            <MainContainer useThemedUI={useThemedUI} />
            {showOverlayContainer && <OverlayContainer />}
            {((useThemedUI && layout.isFooterEnabled) || customFooterHtml) && (
                <AXNUIFooterContainer {...getFooterProps()} />
            )}
        </div>
    );
};